import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'umi';
import {Skeleton, Space} from 'antd';

import * as ServiceRequestSelectors from '@/selectors/serviceRequestSelectors';
import Drawer from '@/components/Drawer';
import {ROUTE_PATHS} from '../../../../config/RoutePaths';
import ServiceRequestDescriptions from '@/components/Kanban/ServiceRequests/ServiceRequestDescriptions';
import AppointmentFooterMeta from '@/components/Kanban/ServiceRequests/AppointmentFooterMeta';
import {usePubSubEvent} from '@/context/PubSubContext/hooks';
import {CALENDAR_EVENT_DELETED, RECURRING_DELETED, RECURRING_UPDATED} from '@/context/PubSubContext/events';
import isEmpty from 'lodash/isEmpty';
import {useHasAccess} from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';

interface CalendarEventDetailsDrawerState {
  calendarEventId?: string;
  open?: boolean;
  action?: string;
  ignoreRouting?: boolean;
  errors?: Record<string, any>;
  toggledAt?: number;
}

const CalendarEventDetailsDrawer: React.FC = () => {
  const drawer = useSelector(ServiceRequestSelectors.selectCalendarEventDetailsDrawer) as CalendarEventDetailsDrawerState;
  const calendarEventId = drawer?.calendarEventId;
  const calendarEvent = useSelector((state: DefaultRootState) => calendarEventId ? ServiceRequestSelectors.selectCalendarEventById(state, calendarEventId) : null);
  const dispatch = useDispatch();

  const open = drawer?.open;
  const canViewCalendarEvents = useHasAccess([PERMISSIONS.CalendarEvents.VIEW]);
  const previousRoute = useRef<string | null>(null);

  const loading = useSelector(
    (state: DefaultRootState) =>
      state.loading.effects['serviceRequest/fetchSingleCalendarEvent'],
  );

  function toggle() {
    dispatch({
      type: 'serviceRequest/toggleCalendarEventDetailsDrawer',
    });
  }

  useEffect(() => {
    if (!drawer?.ignoreRouting) {
      if (open) {
        previousRoute.current = window.location.pathname;
        if (!window.location.pathname.includes('/calendar-events/'))
          window.history.pushState({}, '', `/calendar-events/${calendarEventId}`);
      } else if (previousRoute.current && typeof previousRoute.current === 'string') {
        if (previousRoute.current.includes('/calendar-events/'))
          return window.history.pushState({}, '', ROUTE_PATHS.CALENDAR);
        window.history.pushState({}, '', previousRoute.current);
      }

    }

  }, [open]);


  usePubSubEvent(CALENDAR_EVENT_DELETED, (action) => {
    if (!open) return;
    const deletedCalendarEventId = action?.calendar_event?.id;
    if (calendarEventId !== deletedCalendarEventId) return;

    toggle();
  });

  // Series-scoped changes (apply_to: this_and_following / all) broadcast ONE
  // recurring_* event carrying the series uuid — not calendar_event_deleted per
  // occurrence (a series can span up to 366 rows). Close/refresh this drawer
  // when the open occurrence belongs to the affected series.
  usePubSubEvent(RECURRING_DELETED, (action) => {
    if (!open) return;
    const recurringId = action?.recurring?.id;
    if (!recurringId || calendarEvent?.recurring?.id !== recurringId) return;

    toggle();
  });
  usePubSubEvent(RECURRING_UPDATED, (action) => {
    if (!open) return;
    const recurringId = action?.recurring?.id;
    if (!recurringId || calendarEvent?.recurring?.id !== recurringId) return;

    // The occurrence may have been edited or trimmed away — re-fetch it; a
    // failed fetch lands in drawer.errors, which closes the drawer above.
    if (canViewCalendarEvents) fetchData();
  });

  const fetchData = () => {
    dispatch({
      type: 'serviceRequest/fetchSingleCalendarEvent',
      payload: {
        calendarEventId,
      },
    });
  };

  useEffect(() => {
    if (calendarEventId && drawer?.errors?.[calendarEventId] && open)
      toggle();

  }, [calendarEventId, drawer?.errors, open]);

  useEffect(() => {
    if (open && calendarEventId && canViewCalendarEvents)
      fetchData();

  }, [calendarEventId, open, canViewCalendarEvents]);

  const getTitle = () => {
    if (!calendarEvent) return <F entity={INTL_ENTITY.APPOINTMENT} action={INTL_ACTION.DETAILS} />;

    return (
      <Space>
        <F entity={INTL_ENTITY.APPOINTMENT} action={INTL_ACTION.DETAILS} />
        <small>{calendarEvent.title || calendarEvent.what}</small>
      </Space>
    );
  };


  function getFooter() {
    return (
      <div style={{ textAlign: 'right', opacity: 0.8 }}>
        <AppointmentFooterMeta appointment={calendarEvent} />
      </div>
    );
  }

  return (
    <Drawer
      data-testid="calendar-event-details-drawer"
      width={800}
      footer={getFooter()}
      title={getTitle()}
      open={open}
      destroyOnHidden
      toggledAt={drawer?.toggledAt}
      onOpenChange={toggle}
    >
      {isEmpty(calendarEvent) && loading ? (
        <Skeleton />
      ) : (
        <ServiceRequestDescriptions
          action={drawer?.action}
          loading={loading}
          appointment={calendarEvent}
          onClose={toggle}
          onRefresh={fetchData}
          // A footer action on a calendar event is delete (edit opens its own
          // drawer): close this drawer and refresh the calendar instead of
          // re-fetching the now-removed event.
          onActionEnd={() => {
            toggle();
            dispatch({ type: 'serviceRequest/updateFetchCalendarViewAt' });
          }}
        />
      )}
    </Drawer>
  );
};

export default CalendarEventDetailsDrawer;
